import type {
  GeologicIntervalValidationError,
  GeologicIntervalValidationWarning,
  OperatorSearchResult,
} from 'types/api-responses';
import { formatNumber } from 'utils/number';
import type { CanonicalDefault } from './atoms';
import type { GeologicIntervalRow, GeologicIntervalBoundItem } from './types';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/**
 * Validates a `#rrggbb` hex color. Strict 7-char form only — short hex
 * (`#abc`) and rgba are rejected.
 * @param {string} value - Candidate color string.
 * @return {boolean} Whether the string is a valid 6-digit hex color.
 */
export function isValidHexColor(value: string): boolean {
  return HEX_RE.test(value);
}

/**
 * Title-cases each whitespace-separated word and collapses internal
 * whitespace. Used to normalize "Other…" custom basin entries.
 * @param {string} value - Raw basin name from user input.
 * @return {string} Normalized title-cased basin name.
 */
export function toTitleCaseBasin(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) return word;
      return word[0]!.toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Title-cases as the user types, preserving a trailing space so word
 * boundaries don't reset on every keystroke.
 * @param {string} value - Live input value.
 * @return {string} Title-cased value preserving trailing whitespace.
 */
export function toTitleCaseBasinLiveInput(value: string): string {
  const trailingSpace = /\s$/.test(value) ? ' ' : '';
  return toTitleCaseBasin(value) + trailingSpace;
}

/**
 * Case-insensitive duplicate check.
 * @param {string} candidate - Name to test.
 * @param {ReadonlyArray<string>} existing - Known names.
 * @return {boolean} True when `candidate` matches any `existing` ignoring case.
 */
export function isDuplicateName(
  candidate: string,
  existing: readonly string[]
): boolean {
  const lower = candidate.trim().toLowerCase();
  if (lower.length === 0) return false;
  return existing.some((n) => n.toLowerCase() === lower);
}

/* ── Interval-positioning helpers ─────────────────────────────────────────
 * `GeologicIntervalBoundItem[]` is assumed sorted ASCENDING by
 * positionalIndex (shallowest → deepest). Callers sort once at build time.
 * ----------------------------------------------------------------------- */

/**
 * Builds a `name → positionalIndex` lookup from the bound list.
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound-picker items.
 * @return {Record<string, number>} Map keyed by interval name.
 */
export function buildIntervalNameIndexMap(
  items: readonly GeologicIntervalBoundItem[]
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const item of items) {
    map[item.name] = item.positionalIndex;
  }
  return map;
}

/**
 * Returns the shallowest item (lowest positional index).
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound list.
 * @return {GeologicIntervalBoundItem | null} First item, or null when empty.
 */
export function findShallowestBound(
  items: readonly GeologicIntervalBoundItem[]
): GeologicIntervalBoundItem | null {
  return items[0] ?? null;
}

/**
 * Returns the deepest item (highest positional index).
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound list.
 * @return {GeologicIntervalBoundItem | null} Last item, or null when empty.
 */
export function findDeepestBound(
  items: readonly GeologicIntervalBoundItem[]
): GeologicIntervalBoundItem | null {
  return items[items.length - 1] ?? null;
}

/**
 * Returns the item one step shallower than `target`.
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound list.
 * @param {GeologicIntervalBoundItem | null} target - Reference item; null short-circuits.
 * @return {GeologicIntervalBoundItem | null} Adjacent-above item, or null when none.
 */
export function findAdjacentAboveBound(
  items: readonly GeologicIntervalBoundItem[],
  target: GeologicIntervalBoundItem | null
): GeologicIntervalBoundItem | null {
  if (!target) return null;
  const idx = items.findIndex((i) => i.id === target.id);
  return idx > 0 ? (items[idx - 1] ?? null) : null;
}

/**
 * Returns the item one step deeper than `target`.
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound list.
 * @param {GeologicIntervalBoundItem | null} target - Reference item; null short-circuits.
 * @return {GeologicIntervalBoundItem | null} Adjacent-below item, or null when none.
 */
export function findAdjacentBelowBound(
  items: readonly GeologicIntervalBoundItem[],
  target: GeologicIntervalBoundItem | null
): GeologicIntervalBoundItem | null {
  if (!target) return null;
  const idx = items.findIndex((i) => i.id === target.id);
  return idx >= 0 && idx < items.length - 1 ? (items[idx + 1] ?? null) : null;
}

/**
 * Flags extreme picks that block the auto-fill counterpart: deepest
 * interval as upper bound, or shallowest as lower bound.
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound list.
 * @param {GeologicIntervalBoundItem | null} upper - Currently selected upper bound.
 * @param {GeologicIntervalBoundItem | null} lower - Currently selected lower bound.
 * @return {string | null} Human-readable error or null when the pair is fine.
 */
export function getBoundExtremeError(
  items: readonly GeologicIntervalBoundItem[],
  upper: GeologicIntervalBoundItem | null,
  lower: GeologicIntervalBoundItem | null
): string | null {
  if (items.length === 0) return null;
  const shallowest = findShallowestBound(items);
  const deepest = findDeepestBound(items);
  if (upper && deepest && upper.id === deepest.id) {
    return `No interval exists below "${upper.name}". Choose a different upper bound.`;
  }
  if (lower && shallowest && lower.id === shallowest.id) {
    return `No interval exists above "${lower.name}". Choose a different lower bound.`;
  }
  return null;
}

/**
 * Upper-bound dropdown options — narrowed to the adjacent-above of the
 * selected lower bound when one is picked.
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound list.
 * @param {GeologicIntervalBoundItem | null} selectedLower - Current lower selection.
 * @return {GeologicIntervalBoundItem[]} Items the upper-bound combobox should show.
 */
export function computeUpperBoundOptions(
  items: readonly GeologicIntervalBoundItem[],
  selectedLower: GeologicIntervalBoundItem | null
): GeologicIntervalBoundItem[] {
  if (!selectedLower) return [...items];
  const above = findAdjacentAboveBound(items, selectedLower);
  return above ? [above] : [];
}

/**
 * Lower-bound dropdown options — narrowed to the adjacent-below of the
 * selected upper bound when one is picked.
 * @param {ReadonlyArray<GeologicIntervalBoundItem>} items - Sorted bound list.
 * @param {GeologicIntervalBoundItem | null} selectedUpper - Current upper selection.
 * @return {GeologicIntervalBoundItem[]} Items the lower-bound combobox should show.
 */
export function computeLowerBoundOptions(
  items: readonly GeologicIntervalBoundItem[],
  selectedUpper: GeologicIntervalBoundItem | null
): GeologicIntervalBoundItem[] {
  if (!selectedUpper) return [...items];
  const below = findAdjacentBelowBound(items, selectedUpper);
  return below ? [below] : [];
}

/**
 * Formats a positional index for ComboBox option labels (e.g.
 * "Shale (idx 1.25)"). Up to 4 decimals, trailing zeros trimmed, no
 * thousands grouping so the value reads as an ordinal token.
 * @param {number} n - Positional index value.
 * @return {string} Display string.
 */
export function formatIdx(n: number): string {
  return formatNumber(n, 0, 4, 'decimal', false);
}

/**
 * Adapts a `CanonicalDefault` row into the `GeologicIntervalBoundItem`
 * shape the upper/lower-bound ComboBoxes expect. Used in "Other…" mode.
 * @param {CanonicalDefault} d - Canonical-defaults row.
 * @return {GeologicIntervalBoundItem} ComboBox-ready item.
 */
export function boundFromCanonical(
  d: CanonicalDefault
): GeologicIntervalBoundItem {
  return {
    id: d.geologicInterval,
    name: d.geologicInterval,
    text: `${d.geologicInterval} (idx ${formatIdx(d.positionalIndex)})`,
    positionalIndex: d.positionalIndex,
  };
}

// Shared prefix bits for the two duplicate-warning copy builders.
function formatWarningContext(warning: GeologicIntervalValidationWarning): {
  operatorLabel: string;
  conflictList: string;
} {
  const operatorLabel = warning.operator.allOperators
    ? 'All Operators'
    : (warning.operator.name ?? 'this operator');
  const conflictList =
    warning.conflictingIntervalNames.join(', ') || 'another interval';
  return { operatorLabel, conflictList };
}

/**
 * Short inline `warnText` rendered under the Target Zone Identifier input.
 * "Fact only" form for the inline slot.
 * @param {GeologicIntervalValidationWarning} warning - Warning entry from the validation response.
 * @param {string} targetZoneIdentifier - Trimmed target zone identifier.
 * @param {GeologicIntervalRow} target - Interval being edited (basin context).
 * @return {string} Inline warn-text.
 */
export function buildDuplicateWarnText(
  warning: GeologicIntervalValidationWarning,
  targetZoneIdentifier: string,
  target: GeologicIntervalRow
): string {
  const { operatorLabel, conflictList } = formatWarningContext(warning);
  return `"${targetZoneIdentifier}" already exists for ${operatorLabel} on ${conflictList} in ${target.basinName} Basin.`;
}

/**
 * Long-form subtitle for the duplicate-landing-string warning notification.
 * @param {GeologicIntervalValidationWarning} warning - Warning entry from the validation response.
 * @param {GeologicIntervalRow} target - Interval being edited (basin context).
 * @return {string} The composed inline-notification subtitle.
 */
export function buildDuplicateWarningSubtitle(
  warning: GeologicIntervalValidationWarning,
  target: GeologicIntervalRow
): string {
  const { operatorLabel, conflictList } = formatWarningContext(warning);
  return `This naming convention is already assigned to "${conflictList}" for ${operatorLabel} in ${target.basinName} Basin. You can still add it, but it may cause conflicts.`;
}

/**
 * Builds the subtitle for a blocking validation error on the proposed
 * ONC. Falls back to the raw API message when the structured fields
 * aren't present.
 * @param {GeologicIntervalValidationError} error - Error entry from the validation response.
 * @param {OperatorSearchResult | null} operator - Currently selected operator.
 * @param {string} targetZoneIdentifier - Trimmed target zone identifier.
 * @return {string} The composed inline-notification subtitle.
 */
export function buildValidationErrorSubtitle(
  error: GeologicIntervalValidationError,
  operator: OperatorSearchResult | null,
  targetZoneIdentifier: string
): string {
  if (error.code === 'taken' && operator && targetZoneIdentifier) {
    return `"${targetZoneIdentifier}" is already mapped to ${operator.name} on this interval.`;
  }
  return error.message;
}
